import { PropsWithChildren, useState } from "react";
import { useIntl } from "react-intl";
import {
  Dialog,
  DialogClose,
  DialogContent,
  DialogHeader,
  DialogTitle,
} from "~/components/atoms/Dialog/Dialog";
import { useCheckoutStore } from "~/stores/checkout/useCheckoutStore";
import { cn } from "~/utils";
import { PropsWithClassName } from "~/utils/types";
import ConfirmModal from "../ConfirmModal/ConfirmModal";
import HeyLightContractForm from "./HeyLightContractForm";

type HeyLightContractModalProps = PropsWithClassName<
  PropsWithChildren<{
    open: boolean;
    setOpen: (open: boolean) => void;
    onSuccess: () => void;
    discountedAmount?: number;
  }>
>;

const HeyLightContractModal = ({
  open,
  setOpen,
  className,
  onSuccess,
  discountedAmount,
}: HeyLightContractModalProps) => {
  const [isSubmitting, setIsSubmitting] = useState(false);
  const [showConfirmModal, setShowConfirmModal] = useState(false);
  const intl = useIntl();
  const setHeyLightOrderId = useCheckoutStore(
    (state) => state.setHeyLightOrderId
  );

  const onOpenChange = (open: boolean) => {
    if (!open && isSubmitting) {
      setShowConfirmModal(true);
      return;
    }
    if (!open) {
      // Clean up HeyLight order ID when modal is closed
      setHeyLightOrderId(null);
    }
    setOpen(open);
  };

  const handleSuccess = () => {
    setOpen(false);
    onSuccess();
  };

  return (
    <>
      <ConfirmModal
        open={showConfirmModal}
        setOpen={setShowConfirmModal}
        title={intl.formatMessage({ id: "heylight.create.order.cancel.title" })}
        description={intl.formatMessage({
          id: "heylight.create.order.cancel.description",
        })}
        buttonText={intl.formatMessage({
          id: "heylight.create.order.cancel.cta",
        })}
        buttonAction={() => {
          setShowConfirmModal(false);
          setOpen(false);
        }}
        onCancel={() => setShowConfirmModal(false)}
      />

      <Dialog open={open} onOpenChange={onOpenChange}>
        <DialogContent
          className={cn(
            "max-w-[1400px] min-h-[600px] flex flex-col justify-between max-h-[90vh] overflow-auto bg-white rounded-3xl p-8",
            className
          )}
        >
          <div className="flex flex-col gap-6 grow">
            <DialogHeader>
              <DialogTitle className="text-2xl font-medium">
                {intl.formatMessage({
                  id: "heylight.creditCheck.modal.title",
                })}
              </DialogTitle>
              <DialogClose className="absolute right-8 top-8" />
            </DialogHeader>
            <HeyLightContractForm
              onSuccess={handleSuccess}
              onSubmissionStateChange={setIsSubmitting}
              isOpen={open}
              discountedAmount={discountedAmount}
            />
          </div>
        </DialogContent>
      </Dialog>
    </>
  );
};

export default HeyLightContractModal;
